import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Flex,
  Box,
  Text,
  FormControl,
  Textarea,
  Select,
} from '@chakra-ui/react';
import InvoiceProductSidebar from './InvoiceProductSidebar';
import SelectedInvoiceProductsTable from './SelectedInvoiceProductsTable';
import InvoiceCustomerSelector from './InvoiceCustomerSelector';
import InvoiceSupplierSelector from './InvoiceSupplierSelector';
import api from '../api';

const InvoiceModal = ({ isOpen, onClose, type, invoiceData, onInvoiceCreated }) => {
  const [formData, setFormData] = useState({
    customer: '',
    supplier: '',
    note: '',
    entries: [],
    total_without_vat: 0,
    vat: 0,
    total_with_vat: 0,
    status: 'new',
  });
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({ name: '' });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (invoiceData) {
      setFormData({
        ...invoiceData,
        customer: invoiceData.customer ? invoiceData.customer : undefined,
        supplier: invoiceData.supplier ? invoiceData.supplier : undefined,
        entries: invoiceData.entries.map((entry) => ({
          ...entry,
          product_id: entry.product?.id || entry.product_id, 
          name: entry.product?.name || entry.name,
        })),
      });
    } else {
      setFormData({ customer: undefined, supplier: undefined, note: '', entries: [] }); 
    }
  }, [invoiceData]);

  const fetchProducts = async (page = 1) => {
    setLoadingProducts(true);
    try {
      const response = await api.get(
        `/products/?page=${page}&search=${filters.name}&categories=${selectedCategories.join(',')}`
      );
      const newProducts = response.data.results;
      setProducts((prev) => (page === 1 ? newProducts : [...prev, ...newProducts]));
      setHasMore(!!response.data.next);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    console.log('formData.customer updated:', formData.customer);
  }, [formData.customer]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories/hierarchy/');
      setCategories(response.data); // Hierarchical categories
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers/?all=true');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers/?all=true');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories/hierarchy/');
        setCategories(response.data); // Store hierarchical categories
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };

    const fetchSuppliers = async () => {
      try {
        const response = await api.get('/suppliers/?all=true');
        setSuppliers(response.data);
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
      }
    };
  
    const fetchCustomers = async () => {
      try {
        const response = await api.get('/customers/?all=true');
        setCustomers(response.data);
      } catch (error) {
        console.error('Failed to fetch customers:', error);
      }
    };
    
    fetchSuppliers()
    fetchCustomers();
    fetchCategories(); // Call fetchCategories here
  }, []);
  

  const handleAddProduct = (product) => {
    if (!formData.entries.some((entry) => entry.product_id === product.id)) {
      const markup = type === 'arrival' ? 0 : product.markup_percentage;
      setFormData((prev) => ({
        ...prev,
        entries: [
          ...prev.entries,
          {
            product_id: product.id,
            quantity: 1,
            price: product.price,
            purchase_price: product.purchase_price,
            markup_percentage: markup,
            name: product.name,
          },
        ],
      }));
    }
  };

  const handleProductChange = (productId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.map((entry) =>
        entry.product_id === productId ? { ...entry, [field]: value } : entry
      ),
    }));
  };

  const handleRemoveEntry = (productId) => {
    console.log('Removing product with ID:', productId);
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((entry) => entry.product_id !== productId),
    }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        ...(formData.customer ? { customer: formData.customer } : {}),
        entries: formData.entries.map((entry) => ({
          product_id: entry.product_id,
          quantity: entry.quantity,
          price: entry.price,
          purchase_price: entry.purchase_price,
          markup_percentage: entry.markup_percentage,
        })),
        total_without_vat: totals.totalWithoutVat,
        vat: totals.vat,
        total_with_vat: totals.totalWithVat,
      };
  
      let response;
      if (invoiceData) {
        // Edit existing invoice
        response = await api.put(`/${type}-invoices/${invoiceData.id}/`, payload);
      } else {
        // Create new invoice
        response = await api.post(`/${type}-invoices/`, payload);
        if (onInvoiceCreated) {
          onInvoiceCreated(response.data); // Notify parent component about the new invoice
        }
      }
    
      onClose();
    } catch (error) {
      console.error('Failed to save invoice:', error.response?.data || error.message); // Log backend error
    }
  };

  const handleCloseModal = () => {
    setFormData({ customer: '', note: '', entries: [] });
    setSelectedCategories([]);
    setFilters({ name: '' });
    setProducts([]);
    onClose();
  };

  // Calculate totals
  const calculateTotals = () => {
    const totalWithoutVat = formData.entries.reduce((sum, entry) => {
      const quantity = parseFloat(entry.quantity) || 0;
      const price = type === 'arrival'
        ? parseFloat(entry.purchase_price) || 0
        : parseFloat(entry.price) || 0;
      return sum + quantity * price;
    }, 0);
  
    const vat = totalWithoutVat * 0.2;
    const totalWithVat = totalWithoutVat + vat;
  
    return {
      totalWithoutVat: Number(totalWithoutVat.toFixed(2)) || 0,
      vat: Number(vat.toFixed(2)) || 0,
      totalWithVat: Number(totalWithVat.toFixed(2)) || 0,
    };
  };
  
  
  
  const totals = calculateTotals();
  

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal} isCentered>
      <ModalOverlay />
      <ModalContent maxWidth="90%" h="700px">
        <ModalHeader>{invoiceData ? 'Редагувати накладну' : 'Створити накладну'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" flexDirection="column" h="calc(100% - 120px)">
          <Flex flex="1" overflow="hidden">
            {/* Sidebar */}
            <Box
              flex="1"
              maxW="350px"
              borderRight="1px solid #e2e8f0"
              pr="2"
              overflowY="auto"
              h="100%"
            >
              <InvoiceProductSidebar
                filters={filters}
                setFilters={setFilters}
                categories={categories}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                handleAddProduct={handleAddProduct}
                selectedProducts={formData.entries.map((entry) => entry.product_id)}
              />
            </Box>

            {/* Main Content */}
            <Box flex="3" pl="4" h="100%" display="flex" flexDirection="column">
              {/* Scrollable Table */}
              <Box flex="1" overflowY="auto">
                <SelectedInvoiceProductsTable
                  type={type}
                  entries={formData.entries}
                  products={products}
                  handleProductChange={handleProductChange}
                  handleRemoveEntry={handleRemoveEntry}
                />
              </Box>

              {/* Fixed Client Selector */}
              <Box mt="4" borderTop="1px solid #e2e8f0">
                {type === 'arrival' ? (
                  <InvoiceSupplierSelector
                  suppliers={suppliers}
                  selectedSupplier={formData.supplier}
                  setSelectedSupplier={(id) => setFormData((prev) => ({ ...prev, supplier: id }))}
                  fetchSuppliers={fetchSuppliers}
                />
                ) : (
                  <InvoiceCustomerSelector
                    customers={customers}
                    selectedCustomer={formData.customer}
                    setSelectedCustomer={(id) => setFormData((prev) => ({ ...prev, customer: id }))}
                    fetchCustomers={fetchCustomers}
                  />
                )}
                {/* Note Field */}
                <FormControl mt="2">
                  <Textarea
                    placeholder="Додайте примітку до накладної"
                    value={formData.note}
                    onChange={(e) => setFormData((prev) => ({ ...prev, note: e.target.value }))}
                  />
                </FormControl>
              </Box>
                {/* Totals */}
                <Box textAlign="right" mt="4" borderTop="1px solid #e2e8f0" pt="4">
                    <Text>
                        Разом без ПДВ: {(totals?.totalWithoutVat ? parseFloat(totals.totalWithoutVat) : 0).toFixed(2)} грн
                    </Text>
                    <Text>
                      ПДВ: {formData.entries.some((entry) => entry.product && entry.product.is_vat) ? (totals?.vat ? parseFloat(totals.vat) : 0).toFixed(2) : 0} грн
                    </Text>
                    <Text fontWeight="bold">
                      Всього з ПДВ: {formData.entries.some((entry) => entry.product && entry.product.is_vat) ? (totals?.totalWithVat ? parseFloat(totals.totalWithVat) : 0).toFixed(2) : (totals?.totalWithoutVat ? parseFloat(totals.totalWithoutVat) : 0).toFixed(2)} грн
                    </Text>
                </Box>
            </Box>
          </Flex>
        </ModalBody>

        {/* Footer: Save/Cancel Buttons */}
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Скасувати
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit}>
            Зберегти
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default InvoiceModal;
